import { Badge, BADGE_TYPE_INFO, BADGE_TYPE_NEUTRAL } from '@calendly/ui/components/badge';
import { Button } from '@calendly/ui/components/button';
import { IconCode } from '@calendly/ui/components/glyphs/IconCode';
import { IconButton } from '@calendly/ui/components/icon-button';
import { Popover, PopoverContent, PopoverTrigger } from '@calendly/ui/components/popover';
import { BodyText, LabelText } from '@calendly/ui/components/typography';
import { UiIcon } from '@calendly/ui/components/ui-icon';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useGetUserQuery, useUpdateUserActivationDataMutation } from 'common';
import { clearBrandRefreshActivationData, patchBrandRefreshActivationData } from './brandRefreshActivationData';
import { BRAND_REFRESH_DEBUG_UPDATED_EVENT, clearAllSpotlightsFromActivationData, clearBrandRefreshDebugOverrides, isBrandRefreshDebugActive, isMeetingsOnboardingTourDismissed, notifyBrandRefreshDebugUpdated, readBrandRefreshDebugOverrides, setBrandRefreshDebugCountdownDays, setBrandRefreshDebugForceExpired, setBrandRefreshDebugStartedDaysAgo } from './brandRefreshBetaDebug';
import { getCountdownFromStartedAt } from './brandRefreshCountdown';
import { useBrandRefreshActivationData } from './useBrandRefreshActivationData';
import { useBrandRefreshNavTourState } from '../main_nav/spotlight_tour/useBrandRefreshNavTourState';
import { clearProductRefreshPreviewFromStorage, useProductRefreshPreviewContext } from '../product_refresh/productRefreshPreviewContext';
const panelCss = "p1eldiws";
const actionsCss = "a16ydx7j";
const statusCss = "s1vx6ndp";
const countdownOptionsCss = "c1i30hxq";

/** Hairline rule used by {@link DebugPanelSeparator}, with breathing room above and below. */
const separatorCss = "s1a7zcbw";

/** Thin rule that separates sections of the debug panel (e.g. beta banner vs spotlight tour). */
const DebugPanelSeparator = () => <hr className={separatorCss} />;
const BRAND_REFRESH_DEBUG_COUNTDOWN_PRESETS = [1, 3, 7] as const;

/**
 * Banner countdown debug helpers (`?debug=true`). Also exposed on `window.brandRefreshBetaDebug`.
 */
export const BrandRefreshBetaDebugControls = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [debugRevision, setDebugRevision] = useState(0);
  const {
    blob
  } = useBrandRefreshActivationData({
    skip: !isBrandRefreshDebugActive()
  });
  const {
    setOverride: setProductRefreshPreviewOverride
  } = useProductRefreshPreviewContext();
  const {
    data: user,
    refetch: refetchUser
  } = useGetUserQuery(undefined);
  const [updateActivationData] = useUpdateUserActivationDataMutation();
  const {
    isDismissed: isTourDismissed,
    reset: resetTour,
    dismiss: dismissTour
  } = useBrandRefreshNavTourState();
  const activationData = user?.activation_data;
  const meetingsTourDismissed = isMeetingsOnboardingTourDismissed(activationData);
  const serverStartedAt = blob.banner_started_at;
  const debug = readBrandRefreshDebugOverrides(debugRevision);
  const effectiveStartedAt = debug.startedAt ?? serverStartedAt ?? null;
  useEffect(() => {
    const refreshDebug = () => setDebugRevision(value => value + 1);
    window.addEventListener(BRAND_REFRESH_DEBUG_UPDATED_EVENT, refreshDebug);
    return () => {
      window.removeEventListener(BRAND_REFRESH_DEBUG_UPDATED_EVENT, refreshDebug);
    };
  }, []);
  const {
    daysRemaining,
    isExpired
  } = useMemo(() => getCountdownFromStartedAt(effectiveStartedAt, {
    countdownDays: debug.countdownDays,
    forceExpired: debug.forceExpired
  }), [debug.countdownDays, debug.forceExpired, effectiveStartedAt]);
  const persistStartedDaysAgo = useCallback(async (daysAgo: number) => {
    const startedAt = new Date(Date.now() - daysAgo * 24 * 60 * 60 * 1000).toISOString();
    await updateActivationData(data => patchBrandRefreshActivationData(data, {
      banner_started_at: startedAt
    }));
    setBrandRefreshDebugStartedDaysAgo(daysAgo);
  }, [updateActivationData]);
  const persistStartedNow = useCallback(async () => {
    const startedAt = new Date().toISOString();
    clearBrandRefreshDebugOverrides();
    await updateActivationData(data => patchBrandRefreshActivationData(data, {
      banner_started_at: startedAt
    }));
    notifyBrandRefreshDebugUpdated();
  }, [updateActivationData]);
  const handleStartNow = useCallback(() => {
    persistStartedNow().catch(() => {});
  }, [persistStartedNow]);
  const handleExpire = useCallback(() => {
    persistStartedDaysAgo(debug.countdownDays + 1).catch(() => {});
  }, [debug.countdownDays, persistStartedDaysAgo]);
  const handleSetCountdownDays = useCallback((countdownDays: number) => {
    setBrandRefreshDebugCountdownDays(countdownDays);
  }, []);
  const clearActivationDataAndOverrides = useCallback(async () => {
    clearBrandRefreshDebugOverrides();
    clearProductRefreshPreviewFromStorage();
    setProductRefreshPreviewOverride(false);
    try {
      await updateActivationData(clearBrandRefreshActivationData).unwrap();
      notifyBrandRefreshDebugUpdated();
    } catch {
      await refetchUser();
    }
  }, [refetchUser, setProductRefreshPreviewOverride, updateActivationData]);
  const handleClear = useCallback(() => {
    clearActivationDataAndOverrides().catch(() => {});
  }, [clearActivationDataAndOverrides]);
  const clearAllSpotlights = useCallback(async () => {
    try {
      await updateActivationData(clearAllSpotlightsFromActivationData).unwrap();
    } catch {
      await refetchUser();
    }
  }, [refetchUser, updateActivationData]);
  const handleClearAllSpotlights = useCallback(() => {
    clearAllSpotlights().catch(() => {});
  }, [clearAllSpotlights]);
  useEffect(() => {
    if (!isBrandRefreshDebugActive()) return undefined;
    window.brandRefreshBetaDebug = {
      expireNow: () => setBrandRefreshDebugForceExpired(true),
      startedDaysAgo: (days: number) => {
        setBrandRefreshDebugStartedDaysAgo(days);
        persistStartedDaysAgo(days).catch(() => {});
      },
      setCountdownDays: setBrandRefreshDebugCountdownDays,
      clearOverrides: () => {
        handleClear();
      },
      clearAllSpotlights: () => {
        handleClearAllSpotlights();
      },
      getState: readBrandRefreshDebugOverrides
    };
    return () => {
      delete window.brandRefreshBetaDebug;
    };
  }, [handleClear, handleClearAllSpotlights, persistStartedDaysAgo]);
  const toggleOpen = useCallback(() => {
    setIsOpen(open => !open);
  }, []);
  return <Popover isOpen={isOpen} onClose={() => setIsOpen(false)}>
      <PopoverTrigger>
        <IconButton data-testid="brand-refresh-beta-debug-trigger" size="small" appearance="secondary-outline" title="Brand refresh beta debug" aria-label="Brand refresh beta debug" aria-expanded={isOpen} onClick={toggleOpen}>
          <UiIcon icon={IconCode} size="small" />
        </IconButton>
      </PopoverTrigger>
      <PopoverContent className={panelCss} placement="bottom end" data-testid="brand-refresh-beta-debug-controls">
        <LabelText size="small">Beta banner debug</LabelText>
        <div className={statusCss}>
          <BodyText size="x-small">
            {daysRemaining}
            d left /
            {' '}
            {debug.countdownDays}
            d timer
            {isExpired ? ' (expired)' : ''}
          </BodyText>
        </div>
        <LabelText size="small">Timer (days)</LabelText>
        <div className={countdownOptionsCss}>
          {BRAND_REFRESH_DEBUG_COUNTDOWN_PRESETS.map(countdownDays => <Button key={countdownDays} size="small" decoration={debug.countdownDays === countdownDays ? 'primary' : 'secondary-outline'} onClick={() => handleSetCountdownDays(countdownDays)} data-testid={`brand-refresh-debug-countdown-${countdownDays}d`}>
              {countdownDays}
              d
            </Button>)}
        </div>
        <div className={actionsCss}>
          <Button size="small" decoration="secondary-outline" onClick={handleStartNow}>
            Start now
          </Button>
          <Button size="small" decoration="secondary-outline" onClick={handleExpire}>
            Expire
          </Button>
          <Button size="small" decoration="secondary-outline" onClick={handleClear}>
            Clear
          </Button>
        </div>
        <DebugPanelSeparator />
        <LabelText size="small">Brand refresh spotlight tour</LabelText>
        <div className={statusCss}>
          <Badge type={isTourDismissed ? BADGE_TYPE_NEUTRAL : BADGE_TYPE_INFO}>
            {isTourDismissed ? 'Dismissed' : 'Not dismissed'}
          </Badge>
        </div>
        <div className={statusCss}>
          <BodyText size="x-small">
            Resets state to re-run the tour. Reload the page afterward to replay it. Avoid using while a tour is active.
          </BodyText>
        </div>
        <div className={actionsCss}>
          <Button size="small" decoration="secondary-outline" onClick={resetTour} data-testid="brand-refresh-debug-tour-reset">
            Reset tour
          </Button>
          <Button size="small" decoration="secondary-outline" onClick={dismissTour} data-testid="brand-refresh-debug-tour-dismiss">
            Dismiss tour
          </Button>
        </div>
        <DebugPanelSeparator />
        <LabelText size="small">Spotlights</LabelText>
        <div className={statusCss}>
          <BodyText size="x-small">
            Meetings tour dismissed:
            {' '}
            {meetingsTourDismissed ? 'yes' : 'no'}
          </BodyText>
          <BodyText size="x-small">
            Reload meetings page to re-test. Use ?show_meetings_tour=1 to force-show.
          </BodyText>
        </div>
        <div className={actionsCss}>
          <Button size="small" decoration="secondary-outline" onClick={handleClearAllSpotlights} data-testid="brand-refresh-debug-clear-all-spotlights">
            Clear all spotlights
          </Button>
        </div>
      </PopoverContent>
    </Popover>;
};

require("./BrandRefreshBetaDebugControls.wyw-in-js.css!=!../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./BrandRefreshBetaDebugControls.tsx");